/**
 * useErroTemporario — um erro curto que passa sozinho (A11, 25/10/2026).
 *
 * A janelinha da ligação tem 52 px e uma linha só para o estado: o erro não pode ficar para sempre no lugar do
 * cronômetro. O texto fica `ms` (8 s: um erro precisa de leitura) e sai.
 *
 *  - Erro NOVO recomeça a contagem. O MESMO texto repetido (o `MediaDevicesError` dispara a cada tentativa) só
 *    recomeça o temporizador: o valor não muda, então o nó do `role="alert"` é o mesmo e o leitor de tela não
 *    anuncia de novo.
 *  - As duas funções têm identidade estável: vão como `onErro` para dentro da sala, cuja ponte se inscreve de novo
 *    a cada troca delas.
 *  - O temporizador sai no desmonte, e definir o erro depois dele (a sala cai junto) não agenda nada. Antes da
 *    montagem vale: o erro que um filho avisa no próprio efeito de montagem (síncrono, antes do efeito do pai) entra.
 */
import { useCallback, useEffect, useRef, useState } from "react";

/** Quanto um erro fica na tela antes de sair sozinho. */
export const ERRO_TEMPORARIO_MS = 8_000;

export interface ErroTemporario {
  erro: string | null;
  /** Mostra o texto e (re)começa a contagem, mesmo que o texto seja o que já está na tela. */
  definirErro: (texto: string) => void;
  /** Tira o erro na hora (a causa passou) e cancela a contagem. */
  limparErro: () => void;
}

export function useErroTemporario(ms: number = ERRO_TEMPORARIO_MS): ErroTemporario {
  const [erro, setErro] = useState<string | null>(null);
  const temporizador = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Começa `true`: o efeito de um FILHO roda antes do efeito deste hook (o do pai), e o LiveKit avisa o erro de
  // forma síncrona nesse efeito quando o endereço da sala vem vazio. Com `false` aqui o erro era descartado e a
  // janela ficava em "Conectando…" sem aviso. O efeito abaixo regrava `true` (remontagem) e o desmonte põe `false`.
  const montado = useRef(true);

  const pararContagem = useCallback(() => {
    if (temporizador.current !== null) clearTimeout(temporizador.current);
    temporizador.current = null;
  }, []);

  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
      pararContagem();
    };
  }, [pararContagem]);

  const limparErro = useCallback(() => {
    pararContagem();
    setErro(null);
  }, [pararContagem]);

  const definirErro = useCallback(
    (texto: string) => {
      if (!montado.current) return;
      pararContagem();
      setErro(texto);
      temporizador.current = setTimeout(() => {
        temporizador.current = null;
        setErro(null);
      }, ms);
    },
    [ms, pararContagem]
  );

  return { erro, definirErro, limparErro };
}
